// The new-contract form filled in from a document: first by the rules in ./suggest, then by AI
// (mergeAiSuggestions in ./ai). Used by the app when a file is picked and by the server when a
// document arrives by email, so both fill the form the same way.
import { mergeAiSuggestions, type AiSuggestions } from "./ai";
import { typeRole } from "./catalog";
import { emptyContractFields, type ContractFields } from "./contract";
import { currencyDigits } from "./money";
import type { Suggestions } from "./suggest";

/** Filenames like "scan_0012" or "Document (3)" say nothing; the document's own heading is a better name. */
export const GENERIC_FILENAME = /^(?:scan|img|image|doc|document|file|untitled|download|print|pdf|contract|agreement|copy|new)?[\s\d()_.-]*(?:scan|copy)?[\s\d()_.-]*$/i;

/** Suggestions that fill a form field; parties, rate and title only help fill others. */
export const FIELD_SUGGESTIONS: (keyof Suggestions)[] = ["startDate", "endDate", "noEndDate", "noticePeriodDays", "renewalType", "renewalTermMonths", "counterparty", "value"];

/** How many details the rules filled in: fields, key terms, and the type when it's clear. */
export const rulesFound = (s: Suggestions) => FIELD_SUGGESTIONS.filter((k) => s[k]).length + Object.keys(s.details ?? {}).length + (s.type?.confident ? 1 : 0);

/** "Office lease (signed).pdf" → "Office lease (signed)". */
export const titleFromFilename = (filename: string) => filename.replace(/\.(pdf|docx)$/i, "").replace(/[_-]+/g, " ").trim();

export function suggestedNotes(s: Suggestions): string | null {
  const lines: string[] = [];
  if (s.rate && !s.value) lines.push(`Rate: ${s.rate.value} (from the document).`);
  if (s.noEndDate) lines.push("No fixed end date: the document says it runs indefinitely.");
  if (s.parties && s.parties.value.length > 2) lines.push(`Parties: ${s.parties.value.map((p) => (p.role ? `${p.name} (${p.role})` : p.name)).join("; ")}.`);
  return lines.length ? lines.join("\n") : null;
}

/**
 * Fills `prev` from the rules' suggestions, keeping what's already there except dates the document
 * states. The type is set only when the document clearly is one (otherwise the form offers it); key
 * terms fill in around anything already entered.
 */
export function draftFromRules(prev: ContractFields, s: Suggestions, filename: string): ContractFields {
  const fromFilename = titleFromFilename(filename);
  const type = prev.type || (s.type?.confident ? s.type.value : null);
  const details = { ...prev.details };
  for (const [key, term] of Object.entries(s.details ?? {})) details[key] ??= term.value;
  if (!details.contract_value && s.value) {
    const digits = currencyDigits(s.value.value.currency);
    details.contract_value = (s.value.value.minor / 10 ** digits).toFixed(digits).replace(/\.0+$/, "");
  }
  const role = s.counterparty && s.parties?.value.find((p) => p.name === s.counterparty!.value)?.role;
  return {
    ...prev,
    type,
    title: (prev.title || (s.title && GENERIC_FILENAME.test(fromFilename) ? s.title.value : fromFilename)).slice(0, 200),
    // The other side becomes a person, with the type's name for their role; the total value is the "Total contract value" term.
    parties: prev.parties.length || !s.counterparty ? prev.parties : [{ name: s.counterparty.value, role: typeRole(type, role) ?? (role || "Other party"), email: null, phone: null }],
    details,
    currency: prev.currency ?? s.value?.value.currency ?? (Object.keys(s.details ?? {}).length ? (s.currency?.value ?? null) : null),
    notes: prev.notes ?? suggestedNotes(s),
    startDate: s.startDate?.value ?? prev.startDate,
    endDate: s.endDate?.value ?? prev.endDate,
    noticePeriodDays: s.noticePeriodDays?.value ?? prev.noticePeriodDays,
    renewalType: s.renewalType?.value ?? prev.renewalType,
    renewalTermMonths: s.renewalTermMonths?.value ?? prev.renewalTermMonths,
  };
}

export interface Draft {
  fields: ContractFields;
  /** Fields and terms AI filled, so the form can mark them. */
  aiKeys: string[];
  /** Details AI or the rules filled in, for "we found N details". */
  found: number;
}

/** The whole form from a document: rules first, then AI on top (AI never replaces what the rules found). */
export function buildDraft(s: Suggestions, filename: string, ai?: AiSuggestions | null): Draft {
  const rules = draftFromRules(emptyContractFields(), s, filename);
  const ruleCount = rulesFound(s);
  if (!ai) return { fields: rules, aiKeys: [], found: ruleCount };
  const merged = mergeAiSuggestions(rules, ai);
  // AI's title for a file named "scan_0012" is better than "scan 0012".
  if (ai.title && !s.title && GENERIC_FILENAME.test(titleFromFilename(filename))) merged.fields.title = ai.title.slice(0, 200);
  return { fields: merged.fields, aiKeys: [...merged.aiKeys], found: ruleCount + merged.filled };
}
